/*    
<summary>
  This functional component "AddUpdateCustomerSetting" defines the props like initial values and functions for the child form component.
  Developer:Mohammad Saquib Khan, Created Date:04-June-2024
</summary>
<returns>Returns JSX</returns>
*/

import { observer } from "mobx-react";
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { initialPageLimit } from "../../../../constants/pageLimit.constant";
import { useStore } from "../../../../contexts/store-provider";
import IPageInfo from "../../../../models/ICommon";
import { ISorting } from "../../../../models/ISorting";
import DeletePopup from "../../../../shared-components/popup/delete/delete-popup";
import DeletePopupHOC from "../../../../shared-components/popup/delete/delete-popup-hoc";

import FormLoader from "../../../../shared-components/FormLoader/FormLoader";
import { formatMessage } from "../../../../translations/format-message";
import MotivationListView from "./components/store-motivation-list-view";
import AddMotivationSettingsForm from "./forms/add-store-motivation-settings-form";

interface IProps {
  id: number;
  modalClosed: () => void;
  showDeletePopup: boolean;
  deletePopupToggleHandler: () => void;
}

const MotivationSettings = (props: IProps) => {
  const [isDetailsVisible, setIsDetailsVisible] = useState<boolean>(false);
  const { masterManagementStore } = useStore();
  const {
    addUpdateMotivationSettingState,
    GetAllMotivationService,
    getAllMotivationList,
    motivationSettingState,
    AddMotivationService,
    UpdateMotivationService,
    DeleteMotivationService,
    GetMotivationByIdService,
    deleteMotivationSettingState,
    getByIdMotivationSettingState,
    getMotivationDetail,
    resetGetAllMotivationSettingState,
    resetGetByIdMotivationSettingState,
    resetdDeleteMotivationSettingState,
    resetAddUpdateMotivationSettingState,
  } = masterManagementStore;
  // const initialValues: IAddCustomerRate = addCustomerRateState;
  const initialValues = getMotivationDetail;

  const { id, modalClosed, showDeletePopup, deletePopupToggleHandler } = props;

  const onModalClosed = useCallback(() => {
    modalClosed();
    resetAddUpdateMotivationSettingState();
    resetdDeleteMotivationSettingState();
    resetGetAllMotivationSettingState();
    resetGetByIdMotivationSettingState();
  }, [modalClosed]);

  const setIsRefreshPage = () => {
    setPageInfo({ ...pageInfo, isRefreshPage: false });
  };

  const handleVisibility = () => {
    setIsDetailsVisible((e) => !e);
  };
  const [selectedId, setSelectedId] = useState<number>(-1);
  const [editableId, setEditableId] = useState<number>(-1);
  const [disablePagination, setDisablePagination] = useState<boolean>(false);
  const [pageLimit, setPageLimit] = useState<number>(initialPageLimit);
  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    currentPage: 1,
    totalPages: 1,
    isPagerChange: false,
    isRefreshPage: false,
  });
  const onPageChanged = useCallback((pageNumber: number) => {
    setPageInfo((pageInfo) => ({ ...pageInfo, currentPage: pageNumber }));
  }, []);
  const onPageLimitChanged = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    let value = parseInt(evt.target.value);
    setPageLimit(value);
  };
  const [sorting, setSorting] = useState<ISorting>({
    column: "userName",
    orderBy: "asc",
  });
  const [isApiCalled, setIsApiCalled] = useState<boolean>(false);

  const callGetMotivationList = useCallback(() => {
    setDisablePagination(true);
    GetAllMotivationService();
    setIsApiCalled(true);
  }, [GetAllMotivationService]);

  // useEffect(() => {
  //   setPageInfo((pageInfo) => ({
  //     ...pageInfo,
  //     // totalPages: customerRateList?.TotalPages,
  //     totalPages: 1,
  //   }));
  //   setDisablePagination(false);
  // }, [getAllMotivationList]);
  // }, [getAllMotivationList, customerRateList.TotalPages]);

  const handleSortingChange = (accessor: string) => {
    let sortOrder = sorting.orderBy;
    if (accessor === sorting.column) {
      sortOrder = sorting.orderBy === "asc" ? "desc" : "asc";
    }
    setSorting({ column: accessor, orderBy: sortOrder });
  };

  const onSubmitForm = (values: any) => {
    let finalValues = {
      ...values,
    };
    if (!(values.Id && values.Id > 0)) {
      AddMotivationService(finalValues);
    } else {
      UpdateMotivationService(values.Id, finalValues);
    }
  };

  const deleteHandler = () => {
    DeleteMotivationService(selectedId);
    deletePopupToggleHandler();
    setEditableId(-1);
    resetAddUpdateMotivationSettingState();
  };

  const editHandler = (row: { Id: number }) => {
    setEditableId(row.Id);
    GetMotivationByIdService(row.Id);
  };

  useEffect(() => {
    callGetMotivationList();
  }, [callGetMotivationList]);

  useEffect(() => {
    if (addUpdateMotivationSettingState.success) {
      if (editableId > 0) {
        toast.success(formatMessage("motivation_setting_update_success"));
      } else {
        toast.success(formatMessage("motivation_setting_add_success"));
      }
      callGetMotivationList();
      resetAddUpdateMotivationSettingState();
      setEditableId(-1);
    }
  }, [
    addUpdateMotivationSettingState.success,
    callGetMotivationList,
    resetAddUpdateMotivationSettingState,
    editableId,
  ]);

  useEffect(() => {
    if (deleteMotivationSettingState.success) {
      resetdDeleteMotivationSettingState();
      toast.success(formatMessage("motivation_setting_delete_success"));
      callGetMotivationList();
    }
  }, [
    deleteMotivationSettingState.success,
    callGetMotivationList,
    resetdDeleteMotivationSettingState,
  ]);

  return (
    <React.Fragment>
      {(getByIdMotivationSettingState.inProgress ||
        addUpdateMotivationSettingState.inProgress ||
        deleteMotivationSettingState.inProgress ||
        motivationSettingState.inProgress) && (
        <FormLoader
          loading={
            getByIdMotivationSettingState.inProgress ||
            addUpdateMotivationSettingState.inProgress ||
            deleteMotivationSettingState.inProgress ||
            motivationSettingState.inProgress
          }
        />
      )}

      {showDeletePopup && (
        <DeletePopup
          title="delete_motivation"
          modalSubmit={deleteHandler}
          modalClosed={deletePopupToggleHandler}
          isLoading={deleteMotivationSettingState.inProgress}
          message="delete_motivation_confirm"
        />
      )}

      <AddMotivationSettingsForm
        submitHandler={onSubmitForm}
        addUpdateInProgress={addUpdateMotivationSettingState.inProgress}
        actionSuccess={addUpdateMotivationSettingState.success}
        initialValues={initialValues}
        id={editableId}
        onCancelEdit={() => {
          setEditableId(-1);
          resetAddUpdateMotivationSettingState();
        }}
      />
      <MotivationListView
        setVisiblity={handleVisibility}
        id={id}
        showDeletePopup={showDeletePopup}
        deleteToggleHandler={deletePopupToggleHandler}
        onPageLimitChanged={onPageLimitChanged}
        pageLimit={pageLimit}
        pageInfo={pageInfo}
        onPageChanged={onPageChanged}
        disablePagination={disablePagination}
        sorting={sorting}
        handleSortingChange={handleSortingChange}
        setSelectedId={setSelectedId}
        selectedId={selectedId}
        editHandler={editHandler}
        setIsRefreshPage={setIsRefreshPage}
      />
    </React.Fragment>
  );
};

export default DeletePopupHOC(observer(MotivationSettings));
